// src/pages/Booking/PaymentPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Smartphone, Shield, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import paymentService from '../../services/paymentService';
import PaymentStatus from '../../components/Payment/PaymentStatus';
import { WaveLogo, OrangeMoneyLogo } from '../../components/Payment/PaymentMethodLogos';
import { formatPriceFCFA } from '../../utils/formatters';
import './PaymentPage.css';

function validatePhone(raw) {
  const p = raw.trim().replace(/\s/g, '');
  if (!p) return { ok: false, err: 'Veuillez saisir votre numéro de téléphone' };
  if (p.startsWith('+221') || p.startsWith('00221')) {
    return { ok: false, err: 'Retirez l\'indicatif — 9 chiffres uniquement (ex: 77 123 45 67)' };
  }
  if (!/^7[0678]\d{7}$/.test(p)) {
    return { ok: false, err: 'Numéro invalide — 9 chiffres, préfixe 70/76/77/78' };
  }
  return { ok: true, clean: p };
}

function formatPhoneDisplay(digits) {
  if (!digits || digits.length !== 9) return digits;
  return `+221 ${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 7)} ${digits.slice(7)}`;
}

function extractLocalPhone(phone) {
  if (!phone) return '';
  let p = String(phone).replace(/[\s\-()]/g, '');
  if (p.startsWith('+221')) p = p.slice(4);
  else if (p.startsWith('00221')) p = p.slice(5);
  else if (p.startsWith('221') && p.length === 12) p = p.slice(3);
  return p.replace(/\D/g, '');
}

const PAYMENT_METHODS = [
  {
    id: 'WAVE',
    name: 'Wave',
    subtitle: 'Paiement mobile Wave',
    Logo: WaveLogo,
    className: 'pp-method--wave',
  },
  {
    id: 'ORANGE',
    name: 'Orange Money',
    subtitle: 'Paiement mobile Orange',
    Logo: OrangeMoneyLogo,
    className: 'pp-method--orange',
  },
];

const PaymentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const {
    reservationId,
    depositPercent,
    depositAmount,
    totalPrice,
    salonName,
  } = location.state || {};

  const [step, setStep] = useState('choose');
  const [clientPhone, setClientPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [pendingData, setPendingData] = useState(null);

  useEffect(() => {
    const local = extractLocalPhone(user?.phone);
    if (local && /^7[0678]\d{7}$/.test(local)) {
      setClientPhone(local);
    }
  }, [user]);

  if (!reservationId || !depositAmount) {
    navigate('/');
    return null;
  }

  const phoneValidation = validatePhone(clientPhone);
  const phoneReady = phoneValidation.ok;
  const phoneDisplay = phoneReady ? formatPhoneDisplay(phoneValidation.clean) : null;

  const resetPending = () => {
    paymentService.stopPolling();
    setPendingData(null);
    setStep('choose');
    setErrorMsg('');
  };

  const handlePay = async (method) => {
    const { ok, err, clean } = validatePhone(clientPhone);
    if (!ok) {
      setPhoneError(err);
      return;
    }
    setPhoneError('');
    setErrorMsg('');
    setStep('processing');

    const payResult = await paymentService.initiatePayment(reservationId, method, clean);

    if (!payResult.success) {
      setErrorMsg(payResult.message || 'Erreur lors du paiement');
      setStep('error');
      return;
    }

    if (payResult.status === 'PENDING' && payResult.externalTransactionId) {
      setPendingData({
        externalTransactionId: payResult.externalTransactionId,
        deepLinkUrl: payResult.deepLinkUrl,
        paymentMode: method,
        amount: payResult.amount || depositAmount,
      });
      setStep('pending');
      return;
    }

    setStep('success');
    setTimeout(() => {
      navigate('/booking/confirmation', { state: { reservationId } });
    }, 1800);
  };

  if (step === 'processing') {
    return (
      <div className="pp-page">
        <div className="pp-processing">
          <div className="pp-spinner-wrap">
            <div className="pp-spinner" />
          </div>
          <h2 className="pp-processing-title">Initiation du paiement…</h2>
          <p className="pp-processing-sub">Connexion à Wave / Orange Money</p>
        </div>
      </div>
    );
  }

  if (step === 'pending' && pendingData) {
    return (
      <div className="pp-page">
        <PaymentStatus
          externalTransactionId={pendingData.externalTransactionId}
          deepLinkUrl={pendingData.deepLinkUrl}
          paymentMode={pendingData.paymentMode}
          amount={pendingData.amount}
          onSuccess={() => {
            setStep('success');
            setTimeout(() => {
              navigate('/booking/confirmation', { state: { reservationId } });
            }, 1500);
          }}
          onFailed={() => setStep('error')}
          onRetry={resetPending}
        />
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className="pp-page">
        <div className="pp-success">
          <div className="pp-success-icon">✅</div>
          <h2 className="pp-success-title">Paiement confirmé !</h2>
          <p className="pp-success-sub">Redirection vers votre confirmation…</p>
        </div>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="pp-page">
        <div className="pp-container">
          <div className="pp-error-block">
            <AlertTriangle size={40} strokeWidth={1.5} />
            <h2>Une erreur s'est produite</h2>
            <p>{errorMsg || 'Le paiement n\'a pas pu être confirmé.'}</p>
            <button type="button" className="pp-btn pp-btn--primary" onClick={resetPending}>
              Réessayer
            </button>
            <button type="button" className="pp-btn pp-btn--ghost" onClick={() => navigate(-1)}>
              Retour
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pp-page">
      <div className="pp-container">
        <button type="button" className="pp-back" onClick={() => navigate(-1)}>
          <ArrowLeft size={16} strokeWidth={2.5} />
          <span>Retour</span>
        </button>

        <div className="pp-header">
          <div className="pp-header-icon">
            <Shield size={22} strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="pp-title">Paiement de l'acompte</h1>
            <p className="pp-subtitle">{salonName}</p>
          </div>
        </div>

        <div className="pp-amounts">
          <div className="pp-amount-row">
            <span className="pp-amount-lbl">Total prestation</span>
            <span className="pp-amount-val">{formatPriceFCFA(totalPrice)}</span>
          </div>
          <div className="pp-amount-row pp-amount-row--accent">
            <span className="pp-amount-lbl">
              Acompte requis
              <span className="pp-amount-pct">{depositPercent}%</span>
            </span>
            <span className="pp-amount-val pp-amount-val--big">{formatPriceFCFA(depositAmount)}</span>
          </div>
          <div className="pp-amount-row pp-amount-row--muted">
            <span className="pp-amount-lbl">Reste à payer sur place</span>
            <span className="pp-amount-val">{formatPriceFCFA(totalPrice - depositAmount)}</span>
          </div>
        </div>

        <div className="pp-client-phone-block">
          <label className="pp-client-phone-label" htmlFor="client-phone">
            <Smartphone size={14} strokeWidth={2} />
            Votre numéro Wave / Orange Money
          </label>
          <input
            id="client-phone"
            className={`pp-client-phone-input ${phoneError ? 'pp-client-phone-input--error' : ''}`}
            type="tel"
            placeholder="Ex : 77 123 45 67"
            value={clientPhone}
            onChange={(e) => { setClientPhone(e.target.value); setPhoneError(''); }}
            autoComplete="tel"
          />
          {phoneError && <span className="pp-client-phone-error">{phoneError}</span>}
          <span className="pp-client-phone-hint">
            Vous recevrez une demande de validation sur ce numéro.
          </span>
        </div>

        <div className="pp-notice">
          <div className="pp-notice-dot" />
          <p>
            <strong>Paiement sécurisé</strong> — Validez la transaction sur votre téléphone
            dans les 15 minutes suivant l'initiation.
          </p>
        </div>

        <p className="pp-methods-title">Choisir le moyen de paiement</p>

        <div className="pp-methods">
          {PAYMENT_METHODS.map(({ id, name, subtitle, Logo, className }) => (
            <button
              key={id}
              type="button"
              className={`pp-method ${className}${phoneReady ? '' : ' pp-method--disabled'}`}
              onClick={() => handlePay(id)}
              disabled={!phoneReady}
              aria-disabled={!phoneReady}
            >
              <div className="pp-method-icon">
                <Logo size={46} />
              </div>
              <div className="pp-method-info">
                <span className="pp-method-name">{name}</span>
                <span className="pp-method-number">
                  {phoneDisplay || subtitle}
                </span>
              </div>
              <div className="pp-method-amount">{formatPriceFCFA(depositAmount)}</div>
              <div className="pp-method-arrow">→</div>
            </button>
          ))}
        </div>

        {!phoneReady && (
          <p className="pp-methods-hint">Saisissez votre numéro ci-dessus pour activer le paiement.</p>
        )}

        <div className="pp-guarantee">
          <Shield size={13} strokeWidth={2} />
          <span>Votre acompte est sécurisé et versé au salon après la prestation</span>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
